import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText, Loader2, ShieldCheck, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

// First-time sign-in consent gate. Fetches a country-specific, law-accurate notice from the
// backend and records granular consent (essential required; personalisation & updates optional).
export default function TermsGate() {
  const { user, setUser, country } = useApp();
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opt, setOpt] = useState({ location: true, personalization: true, marketing: false });
  const [adult, setAdult] = useState(false);

  const needsConsent = user && !(user.consent && user.consent.terms);

  useEffect(() => {
    if (!needsConsent) return;
    api.get(`/consent/notice?country=${encodeURIComponent(country || user?.country_code || "")}`)
      .then(({ data }) => setNotice(data))
      .catch(() => setNotice({ law: "your local data-protection law", authority: "your local authority", note: "You consent to processing of your personal data; you can withdraw consent anytime.", purposes: [] }));
  }, [needsConsent, country]);

  if (!needsConsent) return null;

  const accept = async () => {
    if (!adult) { toast.error("Please confirm you are 18 or older."); return; }
    setBusy(true);
    try {
      const payload = { privacy: true, terms: true, marketing: !!opt.marketing, personalization: !!opt.personalization, location: !!opt.location, adult: true, country: notice?.country || country || "" };
      const { data } = await api.post("/me/consent", payload);
      setUser((u) => (u ? { ...u, consent: data.consent } : u));
      toast.success("Thank you 🌿 Welcome to the My Kind family.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save. Try again."); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="terms-gate">
      <div className="rud-card rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 text-brand mb-2"><ScrollText size={18} /><span className="text-[11px] uppercase tracking-widest">Before you continue</span></div>
        <h2 className="font-serif text-2xl">Your Privacy & Consent</h2>
        <p className="text-sm text-sec mt-3">
          My Kind is a kind, hate-free, <b className="text-[var(--rud-text)]">data-sovereign</b> community — you own your data, we <b className="text-[var(--rud-text)]">never sell it</b>, and it's ad-free.
          We collect your <b className="text-[var(--rud-text)]">location with your permission</b> to enable local community, weather, events, nearby members &amp; fair pricing.
          Please review and agree to our <Link to="/terms" className="text-brand underline">Terms</Link> and <Link to="/privacy" className="text-brand underline">Privacy Policy</Link>.
        </p>

        {notice ? (
          <div className="rud-card rounded-xl p-3 mt-3 text-[12px] text-sec" data-testid="terms-region-note">
            <div className="flex items-center gap-1.5 text-brand mb-1"><ShieldCheck size={13} /> <span className="font-medium">{notice.law}</span></div>
            <p>{notice.note}</p>
            {notice.authority ? <p className="mt-1 opacity-80">Regulator: {notice.authority}</p> : null}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sec text-sm mt-3"><Loader2 size={14} className="animate-spin" /> Loading your region's notice…</div>
        )}

        {/* Granular purposes */}
        <div className="mt-3 space-y-2" data-testid="consent-purposes">
          {(notice?.purposes || []).map((p) => {
            const checked = p.required ? true : !!opt[p.key];
            return (
              <button key={p.key} type="button" disabled={p.required}
                data-testid={`consent-${p.key}`}
                onClick={() => !p.required && setOpt((o) => ({ ...o, [p.key]: !o[p.key] }))}
                className={`w-full text-left flex items-start gap-2.5 rounded-xl border p-3 transition-colors ${checked ? "border-brand/40 bg-brand/5" : "border-rud"} ${p.required ? "opacity-90 cursor-default" : ""}`}>
                <span className={`mt-0.5 w-4 h-4 rounded grid place-items-center shrink-0 ${checked ? "bg-brand text-black" : "border border-sec"}`}>{checked ? <Check size={12} /> : null}</span>
                <span>
                  <span className="text-[13px] text-[var(--rud-text)] font-medium">{p.label}{p.required ? <span className="text-[10px] text-brand ml-1">(required)</span> : ""}</span>
                  <span className="block text-[11px] text-sec">{p.desc}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* 18+ confirmation (DPDP — no under-18 processing at soft launch) */}
        <button type="button" data-testid="consent-adult" onClick={() => setAdult((v) => !v)}
          className={`w-full text-left flex items-start gap-2.5 rounded-xl border p-3 mt-2 transition-colors ${adult ? "border-brand/40 bg-brand/5" : "border-rud"}`}>
          <span className={`mt-0.5 w-4 h-4 rounded grid place-items-center shrink-0 ${adult ? "bg-brand text-black" : "border border-sec"}`}>{adult ? <Check size={12} /> : null}</span>
          <span>
            <span className="text-[13px] text-[var(--rud-text)] font-medium">I am 18 years or older <span className="text-[10px] text-brand ml-1">(required)</span></span>
            <span className="block text-[11px] text-sec">My Kind is for adults during launch. We don't knowingly process children's data.</span>
          </span>
        </button>

        <button data-testid="terms-accept" onClick={accept} disabled={busy || !notice || !adult}
          className="btn-primary w-full h-11 rounded-full mt-4 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : null} I Agree & Continue
        </button>
        <p className="text-[10px] text-sec text-center mt-2">Your consent is recorded with a timestamp. You can withdraw it, nominate someone, raise a grievance or delete your data anytime from <Link to="/data-rights" className="text-brand underline">Privacy & Your Data</Link>.</p>
      </div>
    </div>
  );
}
